/**
 * Thin wrapper around RunbookGaps that injects the learning section tabs.
 *
 * RunbookGaps.jsx is owned by another agent and must not be edited.
 * It renders a full page shell (sidebar + content area).  We inject the
 * section tabs into its content area via a React portal: after the DOM
 * commit we locate the header row inside the content area and insert a
 * container node right after it, then portal the tabs into that node.
 *
 * This is intentionally the only place that uses DOM injection for tabs;
 * MyTrails and Corrections include <LearningTabs /> directly.
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import RunbookGaps from "./RunbookGaps";
import LearningTabs from "./LearningTabs";

const RunbookGapsPage = (props) => {
  const wrapperRef = useRef(null);
  const [portalTarget, setPortalTarget] = useState(null);

  useLayoutEffect(() => {
    if (!wrapperRef.current) return;

    // RunbookGaps renders:
    //   div.flex.h-screen > AdminSideBar > div.flex-1 > ... > div.w-full.min-w-0
    //     > div.flex.items-center.justify-between.mb-6  (header row)
    //     > ...content...
    //
    // We insert the tabs container immediately after the header row.
    const contentArea = wrapperRef.current.querySelector(
      ".flex-1.relative .w-full.min-w-0"
    );
    if (!contentArea) return;

    const headerRow = contentArea.querySelector(
      ".flex.items-center.justify-between.mb-6"
    );
    if (!headerRow) return;

    const container = document.createElement("div");
    contentArea.insertBefore(container, headerRow.nextSibling);
    setPortalTarget(container);

    return () => {
      if (container.parentNode) container.parentNode.removeChild(container);
    };
  }, []);

  return (
    <div ref={wrapperRef} className="w-full min-w-0">
      <RunbookGaps {...props} />
      {portalTarget && createPortal(<LearningTabs />, portalTarget)}
    </div>
  );
};

export default RunbookGapsPage;
